import { preventDefault, useOverflowScrollIndicator } from '@/utils'
import { Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  useImperativeHandle,
  useState,
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import {
  useFormState,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form'
import { Button } from './ui'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'
import { cn } from '@/lib/utils'

interface NavButton {
  show: boolean
  text: string
  disabled?: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
}

export interface FormDialogRef {
  open(): void
  close(): void
}

interface Props<TValues extends Record<string, any>> extends PropsWithChildren {
  ref?: React.Ref<FormDialogRef>
  className?: string

  form: UseFormReturn<TValues>

  trigger: ReactNode
  header: string
  description?: string

  noCloseOnEscape?: boolean

  noCloseButton?: boolean
  noCancelButton?: boolean
  noConfirmButton?: boolean

  cancelText?: string
  confirmText?: string
  confirmDisabled?: boolean
  error?: ReactNode

  prevButton?: NavButton
  nextButton?: NavButton

  onClose?(): boolean | void
  onOpen?(): boolean | void
  onOpenChange?(open: boolean): boolean | void
  onSubmit: SubmitHandler<TValues>
  onSubmitInvalid?: SubmitErrorHandler<TValues>
}

export function FormDialog<TValues extends Record<string, any>>({
  children,
  className,
  ref,

  form,

  trigger,
  header,
  description,

  noCloseOnEscape = false,

  noCloseButton = false,
  noCancelButton = false,
  noConfirmButton = false,

  cancelText,
  confirmText,
  confirmDisabled = false,
  error,

  prevButton,
  nextButton,

  onClose,
  onOpen,
  onOpenChange,
  onSubmit,
  onSubmitInvalid,
}: Props<TValues>) {
  const translations = useTranslate({
    cancel: 'common.cancel',
    confirm: 'common.confirm',
  })

  const {
    ref: overflowRef,
    showTopShadow,
    showBottomShadow,
  } = useOverflowScrollIndicator()

  const [open, setOpen] = useState(false)

  const { isValid, isSubmitting } = useFormState({ control: form.control })
  const hasFooter = !noCancelButton || !noConfirmButton

  function handleOpenChange(open: boolean) {
    let proceed = true
    if (!open && !!onClose) proceed = onClose() !== false
    else if (open && !!onOpen) proceed = onOpen() !== false

    if (!!onOpenChange) proceed = onOpenChange(open) !== false

    if (proceed) setOpen(open)
  }

  useImperativeHandle(ref, () => ({
    open: () => handleOpenChange(true),
    close: () => handleOpenChange(false),
  }))

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent
        className={className}
        showCloseButton={!noCloseButton}
        onEscapeKeyDown={preventDefault(noCloseOnEscape)}
      >
        <DialogHeader className="px-1">
          <DialogTitle className="flex items-center gap-2">
            {header}

            {isSubmitting && <Loader.Spinner size={16} />}
          </DialogTitle>

          {!!description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{header}</DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit, onSubmitInvalid)}>
          <div className="relative max-h-[75vh]">
            <div
              ref={overflowRef}
              className="overflow-y-auto max-h-[75vh] px-1 pb-1"
            >
              {children}
            </div>

            <div
              className={cn(
                'transition-opacity opacity-100',
                !showTopShadow && 'opacity-0',
                'pointer-events-none absolute inset-x-0 top-0 h-12 bg-linear-to-b from-white to-transparent'
              )}
            />

            <div
              className={cn(
                'transition-opacity opacity-100',
                !showBottomShadow && 'opacity-0',
                'pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-linear-to-t from-white to-transparent'
              )}
            />
          </div>

          {!!error && (
            <div className="w-full my-4 px-2 text-red-600 text-sm">{error}</div>
          )}

          {hasFooter && (
            <DialogFooter className="mt-6 px-1">
              {!noCancelButton && (
                <DialogClose asChild>
                  <Button type="button" variant="outline" size="lg">
                    {cancelText ?? translations.cancel}
                  </Button>
                </DialogClose>
              )}

              {!!prevButton?.show && (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={prevButton.disabled}
                  onClick={prevButton.onClick}
                >
                  {prevButton.text}
                </Button>
              )}

              {!!nextButton?.show && (
                <Button
                  type="button"
                  size="lg"
                  disabled={nextButton.disabled}
                  onClick={nextButton.onClick}
                >
                  {nextButton.text}
                </Button>
              )}

              {!noConfirmButton && !nextButton?.show && (
                <Button
                  type="submit"
                  size="lg"
                  disabled={!isValid || isSubmitting || confirmDisabled}
                >
                  {confirmText ?? translations.confirm}
                </Button>
              )}
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}
